import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import axios from '../../services/api'
import { useStepContext } from '../../context/steps/StepsProvider'
import { useDataProviderContext } from '../../context/dataProvider/DataProvider'
import { useModalContext } from '../../context/modal/modal'
import { useTranslation } from 'react-i18next'
import { useAuthContext } from '../../context/auth/auth'
import { useTurnstile } from '../../hooks/useTurnstile'
import {
  ChallengeFailedError,
  TURNSTILE_FIELD,
  takeChallengeToken,
} from '../../services/turnstile/challenge'
import StepButton from './StepButton'
import Spinner from '../UI/Spinner'
import Divider from '../UI/Divider'
import FormikErrorText from '../Formik/FormikErrorText'

interface Props {
  hasNotError?: boolean
}

const StepsButtons = ({ hasNotError }: Props) => {
  const { t, i18n } = useTranslation()
  const { setUniqueCode } = useAuthContext()
  const reportData = useDataProviderContext()
  const { step, setStep } = useStepContext()
  const { setShowSubmittedReportModal } = useModalContext()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const isLastStep = step === 4
  const isFirstStep = step === 1

  // Each step mounts its own buttons, so the widget only runs on the review
  // step, where the report is actually sent.
  const turnstile = useTurnstile({
    siteKey: isLastStep ? process.env.REACT_APP_TURNSTILE_SITE_KEY : undefined,
    action: 'report_submit',
    // The widget speaks ISO 639-1; the detector may report 'el-GR'.
    language: i18n.language?.split('-')[0],
  })

  const nextStep = () => {
    if (step === 3 || hasNotError) {
      setStep(step + 1)
    }
  }

  const prevStep = () => {
    setStep(step - 1)
  }

  const postData = async () => {
    if (reportData.companyCode === null) return

    try {
      setIsLoading(true)
      const captchaToken = await takeChallengeToken(turnstile.getToken)
      const reportFormData = new FormData()
      reportFormData.append('company_code', reportData.companyCode)
      reportFormData.append('user_visibility', reportData.anonymity)
      reportFormData.append('category', reportData.category.toString())
      reportFormData.append('incident_date', reportData.incidentDate)
      reportFormData.append(
        'is_active',
        reportData.ongoing === 'yes' ? JSON.stringify(true) : JSON.stringify(false)
      )
      reportFormData.append('incident_location', reportData.incidentOccur)
      reportFormData.append('involved_name_capacity', reportData.involved)
      reportFormData.append('full_description', reportData.description)
      reportFormData.append('status', 'UNASSIGNED')

      if (reportData.anonymity === 'CONFIDENTIAL') {
        reportFormData.append('name', reportData.reporterName)
        reportData.reporterEmail && reportFormData.append('email', reportData?.reporterEmail)
        reportData.reporterPhone && reportFormData.append('phone_number', reportData.reporterPhone)
      }

      if (reportData.branch) {
        reportFormData.append('branch', reportData.branch.id)
      }

      if (captchaToken) {
        reportFormData.append(TURNSTILE_FIELD, captchaToken)
      }

      if (reportData.media?.length !== 0)
        for (const file of reportData.media) reportFormData.append('media', file.file)

      const response = await axios.post('/report/report/', reportFormData)

      if (response.status === 201) {
        setShowSubmittedReportModal(true)
        setUniqueCode(response.data.data.token)
      }
    } catch (error) {
      toast.error(
        error instanceof ChallengeFailedError ? t('humanVerificationFailed') : 'Failed to submit report.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => () => setIsLoading(false), [])

  return (
    <>
      <Divider />
      {isLastStep && (
        <div className='mb-6'>
          <div ref={turnstile.containerRef} />
          {turnstile.hasFailed && <FormikErrorText errorText={t('humanVerificationFailed')} />}
        </div>
      )}
      <div className='flex justify-between gap-6 flex-wrap'>
        {!isFirstStep && (
          <StepButton as='back' type='button' text={`← ${t('back')}`} onClickHandler={prevStep} />
        )}

        <StepButton
          as='next'
          type='submit'
          text={
            isLastStep && isLoading ? (
              <Spinner spinner='form' />
            ) : isLastStep && !isLoading ? (
              t('submitReport')
            ) : (
              t('nextStep')
            )
          }
          onClickHandler={isLastStep ? postData : nextStep}
          hasNotError={step === 3 ? false : hasNotError}
          disabled={isLastStep && (isLoading || !turnstile.isVerified)}
        />
      </div>
    </>
  )
}

export default StepsButtons
